import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, IObjectData, ISyncField, TSyncfield} from "../../../../index";
import {KOSubscriptionTracker} from "../../../../util/KOSubscriptionTracker";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../../../constants";
import {nsSyncFields} from "../../../../req/nsSyncFields";
import {IRequestObject} from "../../../../req/index";
import {getObjectDisplayName, getRawSyncFieldValue, getSourceTranslation, getSyncField} from "../../../../util/misc";
import PromiseRequest from "../../../../req/PromiseRequest";
import {getSearchObjectParams} from "../../../../util/request";
import {nsRuleset} from "../../../ruleset/nsRuleset";
import {TSyncfield_User} from "../../../../constants/Syncfields";
import {ISortableTableParams} from "../../../sortable/index";
import {storeObservable, TStoreId} from "../../../../util/sessionstore";
import {IDialogOpt} from "../../../dialog/manager";
import {global} from "../../../../appContext";
import {NO_DATA_MESSAGE, REQUEST_MESSAGE, VALIDATING_MESSAGE} from "../../../../constants/Language";
import {IComboboxV2Options} from "../../../combobox/v2";
import {createBaseTextMatch} from "../../../combobox/v2-sources";
import {ButtonManager} from "../../../../util/ButtonManager";
import {getCancelButton, getContinueButton, getPreviousButton} from "../../../../constants/RouteDefinitions";
import {nsRoute} from "../../../../util/route";
import ButtonOrder = nsRoute.ButtonOrder;
import pasteHtml from './paste.html?raw';

export let template = pasteHtml;

interface IParams {
    __dialog: IDialogOpt
}

type TStep = 'Form' | 'Loading' | 'Validate'

export class viewModel {
    private ObjectTypeId = ObjectTypeId
    private data: ISyncField[] = [];
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly req_getsyncfields: IRequestObject = nsSyncFields.getSyncFields(ObjectTypeId.User)
    private readonly tag: KnockoutObservable<string | null> = ko.observable(null)
    private readonly tags: KnockoutObservableArray<string> = ko.observableArray<string>([])
    private readonly tagFocus: KnockoutObservable<boolean> = ko.observable(false);
    private readonly step: KnockoutObservable<TStep | null> = ko.observable(null)
    private readonly reqSuccess: KnockoutObservableArray<IObjectData> = ko.observableArray<IObjectData>([])
    private readonly reqFailed: KnockoutObservableArray<string> = ko.observableArray<string>([])
    private readonly req_searchobject: PromiseRequest = new PromiseRequest('searchobject')
    private allSyncfields: ISyncField[] = [];
    private readonly syncfield: KnockoutObservable<ISyncField | null> = ko.observable(null)
    private syncfieldComboboxOptions: IComboboxV2Options<ISyncField> | null = null
    private readonly pageButtons: ButtonManager = new ButtonManager()

    constructor(private params: IParams) {
        this.pageButtons.add(getCancelButton(() => this.params.__dialog.close?.(), {
            validator: () => this.step() === 'Form' || this.step() === 'Validate',
        }))
        this.pageButtons.add(getContinueButton({
            onClick: () => this.onValidateClick(),
            enabled: () => !!this.tags()?.length,
            validator: () => this.step() === 'Form',
        }))
        this.pageButtons.add(getPreviousButton({
            onClick: () => this.onPreviousClick(),
            validator: () => this.step() === 'Validate',
        }))
        this.pageButtons.add(getContinueButton({
            label: () => `((add)) ((user|ac|n:${this.reqSuccess()?.length ?? 0}))`,
            labelBracketInfo: () => this.reqSuccess()?.length ?? 0,
            onClick: () => this.onSubmitClick(),
            enabled: () => !!this.reqSuccess()?.length,
            order: ButtonOrder.SUBMIT,
            validator: () => this.step() === 'Validate',
        }))
        this.init()
    }

    private init() {
        this.reqSuccess([])
        this.reqFailed([])
        this.tags([])
        this.tag(null)
        this.messageData({message: REQUEST_MESSAGE})

        this.req_getsyncfields
            .request
            .then((data: ISyncField[]) => {
                this.allSyncfields = data
                const filter: ISyncField['name'][] = [
                    'Email',
                    'EmployeeNumber',
                    'Mail',
                    'Socialsecuritynumber',
                    'Upn',
                    'UserId',
                    'UserName',
                ]
                this.data = data.filter(s => filter.includes(s.name))
                const labelFn = (o: ISyncField) => getSourceTranslation(o)
                this.syncfieldComboboxOptions = {
                    data: this.data,
                    selected: this.syncfield,
                    emptyPreparedListMessage: NO_DATA_MESSAGE,
                    restoreLastValidOnBlur: true,
                    initialMatch: o => o.name == (this.syncfield()?.name ?? 'Socialsecuritynumber'),
                    option: {
                        label: labelFn,
                        match: createBaseTextMatch(labelFn),
                        key: (o) => o.name,
                    },
                    prepareData: {
                        sort: (items) => _.sortBy(items, labelFn),
                    },
                }
                this.messageData(null)
                this.step('Form')
            })
    }

    private sortableTableParams = ko.pureComputed((): ISortableTableParams<IObjectData> | null => {
        const syncfield = this.syncfield()
        if (!syncfield) {
            return null
        }
        return {
            TABLE: [{
                key: 'Id',
                label: getSourceTranslation(getSyncField('Id', this.allSyncfields)!),
                sort: true,
            }, {
                key: 'GivenName',
                label: getSourceTranslation(getSyncField('GivenName', this.allSyncfields)!),
                sort: true,
            }, {
                key: 'Surname',
                label: getSourceTranslation(getSyncField('Surname', this.allSyncfields)!),
                sort: true,
            }, {
                key: syncfield.name,
                label: getSourceTranslation(syncfield),
                sort: true,
            }],
            getCellText: (key, _val, obj) => getRawSyncFieldValue(key as TSyncfield, obj.syncfields) ?? '',
            sortcol: ko.observable(syncfield.name as any),
            data: this.reqSuccess,
        };
    })

    onSubmit() {
        const tag = this.tag()
        if (!tag) {
            return
        }
        const arr = tag.split(',').filter(s => !!s)
        const newArr = _([...this.tags(), ...arr]).uniq().sort().value()
        this.tags(newArr)
        if (arr.length) {
            const syncfield = this.syncfield()
            const syncfieldName = syncfield ? getSourceTranslation(syncfield) : ''
            const what = `${arr.length} ${syncfieldName}`
            const message = `added subject|s:${what}`
            global.snackbarData({message: `${message}`})
        }
        this.tag('')
    }

    private onPaste(vm: viewModel, evt: JQuery.Event) {
        const e = (evt as any).originalEvent as ClipboardEvent
        const inp = e.target as HTMLInputElement
        const inpVal = inp.value
        const startPos = inp.selectionStart ?? 0;
        const endPos = inp.selectionEnd ?? 0;
        const pasteVal = e.clipboardData
            ?.getData('text')
            .split(/[\r\n]+/gm)
            .filter(Boolean)
            .join(',')
            .trim() ?? ''
        const newVal: string = inpVal.substring(0, startPos)
            + pasteVal
            + inpVal.substring(endPos, inp.value.length);
        inp.value = newVal
        this.tag(newVal)
        const newCaretPos = startPos + pasteVal.length;
        inp.setSelectionRange(newCaretPos, newCaretPos)
    }

    private onEditTagClick(tag: string) {
        this.tags.remove(tag)
        this.tag(String(tag))
        this.tagFocus(true)
    }

    private onValidateClick() {
        global.snackbarData(null)
        this.step('Loading')
        this.initValidate()
    }

    private onPreviousClick() {
        this.step('Form')
    }

    private initValidate() {
        this.messageData({message: VALIDATING_MESSAGE})
        const syncfield = this.syncfield()
        if (!syncfield) {
            return
        }

        Promise.resolve()
            .then(() => {
                const ruleset = nsRuleset.generateRuleset({
                    rules: [nsRuleset.generateRule({
                        argument: this.tags().map(o => o.toLowerCase()).join(','),
                        argumentoperator: 'Er lik en av',
                        attribute: `[LOWER;[${syncfield.name}]]`,
                    })]
                })

                const storeId: TStoreId = `search.object.syncfields${ObjectTypeId.User}`;
                const syncFieldNames: TSyncfield_User[] = storeObservable(storeId)().concat(['GivenName', 'Surname', 'Id', syncfield.name as TSyncfield_User]);
                const syncFields: ISyncField[] = syncFieldNames
                    .map(name => getSyncField(name, this.allSyncfields))
                    .filter((sf): sf is ISyncField => !!sf)

                return this.req_searchobject.request(getSearchObjectParams({
                    jruleset: JSON.stringify(ruleset),
                    resultsprpage: MAX_RESULTS_PER_PAGE,
                    searchcols: syncfield.name,
                    syncfieldids: syncFields.map(sf => sf.syncfieldid).join(','),
                }))
                    .then((data: IObjectData[]) => {
                        this.messageData(null)
                        const missing = _.difference(
                            this.tags().map(o => o.toLowerCase()),
                            data.map(o => getRawSyncFieldValue(syncfield.name, o.syncfields) ?? '').map(o => o.toLowerCase())
                        )
                        this.reqFailed(missing)
                        this.reqSuccess(data)
                        this.step('Validate')
                    })
            })
            .catch((_reason: string) => {
                this.params.__dialog.close?.()
            })
    }

    private onSubmitClick() {
        this.params.__dialog.callbackFn?.(this.reqSuccess())
        this.params.__dialog.close?.()
    }

    private getObjectDisplayName = getObjectDisplayName
    private getRawSyncFieldValue = getRawSyncFieldValue
    private getSourceTranslation = getSourceTranslation

    dispose() {
        this.req_getsyncfields.promise.abort()
        this.kosub.unsuscribeAll()
        this.req_searchobject.abort()
    }
}
